import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UploadButton from '../../components/ul/UploadButton';
import FileUpload from '../../components/ul/FileUpload';
import ResetButton from '../../components/ul/ResetButton';
import NotFound from '../../components/ul/NotFound';
import LottieLoad from '../../components/LotteiLoad';

// Import icons
import { FiEdit2, FiCheckCircle, FiXCircle, FiFile, FiClock, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { HiOutlineUpload, HiOutlineDocumentText } from 'react-icons/hi';
import { VscJson } from 'react-icons/vsc';

const JsonManager = () => {
    const [loader, setLoader] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [replacementFile, setReplacementFile] = useState({});
    const [error, setError] = useState(null);
    const [jsonFiles, setJsonFiles] = useState([]);
    const [editMode, setEditMode] = useState({});
    const [newFileName, setNewFileName] = useState('');
    const navigate = useNavigate();

    // New states for editing
    const [editingName, setEditingName] = useState({});
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleString());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const allowedFileTypes = ['application/json'];
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

    useEffect(() => {
        fetchJsonFiles();

        // Update current time every minute
        const timeInterval = setInterval(() => {
            setCurrentTime(new Date().toLocaleString());
        }, 60000);

        return () => clearInterval(timeInterval);
    }, []);

    const fetchJsonFiles = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getJsonManage();
            if (resp?.status) {
                setJsonFiles(resp?.data);
                setLastUpdated(new Date());
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const resp = await Calling.getJsonManage();
            if (resp?.status) {
                setJsonFiles(resp?.data);
                setLastUpdated(new Date());
                // toast.success("Data refreshed successfully");
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Refresh error:", error.response?.data || error.message);
            toast.error("Failed to refresh data");
        } finally {
            setIsRefreshing(false);
        }
    };

    const validateJsonFile = (file) => {
        if (!file) {
            return { isValid: false, error: 'No file selected.' };
        }

        if (!allowedFileTypes.includes(file.type)) {
            return { isValid: false, error: 'Invalid file type. Only JSON files are allowed.' };
        }

        if (file.size > MAX_FILE_SIZE) {
            return { isValid: false, error: 'File size exceeds the 1 MB limit.' };
        }

        return { isValid: true, error: null };
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        setError(null);
        setSelectedFile(null);

        const validation = validateJsonFile(file);
        if (!validation.isValid) {
            setError(validation.error);
            toast.error(validation.error);
            return;
        }

        // Validate JSON structure
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                JSON.parse(e.target.result);
                setSelectedFile(file);
            } catch (error) {
                const errorMsg = 'Invalid JSON structure. Please check your file.';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        };
        reader.readAsText(file);
    };

    const handleReplacementFileChange = (event, fileId) => {
        const file = event.target.files[0];

        // Remove any previous error for this file
        setReplacementFile(prev => ({
            ...prev,
            [fileId]: { file: null, error: null }
        }));

        const validation = validateJsonFile(file);
        if (!validation.isValid) {
            setReplacementFile(prev => ({
                ...prev,
                [fileId]: { file: null, error: validation.error }
            }));
            toast.error(validation.error);
            return;
        }

        // Validate JSON structure
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                JSON.parse(e.target.result);
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file, error: null }
                }));
            } catch (error) {
                const errorMsg = 'Invalid JSON structure. Please check your file.';
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file: null, error: errorMsg }
                }));
                toast.error(errorMsg);
            }
        };
        reader.readAsText(file);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setError(null);
        setNewFileName('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            setError('Please select a valid JSON file before submitting.');
            return;
        }

        // Check if name is provided
        const fileName = newFileName.trim() || selectedFile.name || 'Unnamed JSON';

        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('image', selectedFile);
            formData.append('name', fileName);

            const resp = await Calling.addJsonManager(formData);

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
            await fetchJsonFiles();
        }
    };

    const handleEditToggle = (fileId) => {
        setEditMode({
            ...editMode,
            [fileId]: !editMode[fileId]
        });

        // Initialize editing name with current name
        if (!editMode[fileId]) {
            const file = jsonFiles.find(f => f._id === fileId);
            setEditingName({
                ...editingName,
                [fileId]: file?.j_name || ''
            });

            // Reset any replacement file
            setReplacementFile(prev => ({
                ...prev,
                [fileId]: { file: null, error: null }
            }));
        }
    };

    const handleNameChange = (fileId, value) => {
        setEditingName({
            ...editingName,
            [fileId]: value
        });
    };

    const handleUpdate = async (fileId) => {
        setLoader(true);
        try {
            const formData = new FormData();

            // Add name if it was edited
            if (editingName[fileId]) {
                formData.append('name', editingName[fileId]);
            }

            // Add replacement JSON file if provided
            if (replacementFile[fileId]?.file) {
                formData.append('image', replacementFile[fileId].file);
            }

            // Call the update API
            const resp = await Calling.updateJsonmanager(fileId, formData);

            if (resp?.status) {
                toast.success("JSON file updated successfully");
                setEditMode({
                    ...editMode,
                    [fileId]: false
                });
                // Reset replacement file after successful update
                setReplacementFile(prev => ({
                    ...prev,
                    [fileId]: { file: null, error: null }
                }));
                await fetchJsonFiles();
            } else {
                toast.error(resp?.message || "Update failed");
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Something went wrong during update.");
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            {loader ? (
                <Loader />
            ) : (
                <div className='w-full text-main-font'>
                    <div className='bg-main-white rounded-xl pb-2 shadow-lg'>
                        <div className='p-3 sm:p-6'>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3 mb-5">
                                {/* Title with Icon and Badge */}
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-bold flex items-center gap-3 text-main-font">
                                        <div className="relative p-2.5 bg-main-blue rounded-xl shadow-lg">
                                            <VscJson className="text-white text-xl" />
                                            {jsonFiles.length > 0 && (
                                                <span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-main-exBlue text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-md">
                                                    {jsonFiles.length > 99 ? '99+' : jsonFiles.length}
                                                </span>
                                            )}
                                        </div>
                                        <span>JSON Manager</span>
                                    </h1>
                                </div>

                                {/* Actions - Last Updated & Refresh */}
                                <div className="flex items-center gap-3">
                                    <div className="bg-main-white px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center gap-2">
                                        <AiOutlineClockCircle className="text-main-subfont" size={16} />
                                        <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                                    </div>
                                    <button
                                        onClick={handleRefresh}
                                        disabled={isRefreshing}
                                        className={`bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg shadow-sm border border-main-blue text-sm font-medium flex items-center gap-2 transition-all duration-200 ${isRefreshing ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                                        title="Refresh Data"
                                    >
                                        <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={16} />
                                    </button>
                                </div>
                            </div>
                            <hr className='border-main-border' />

                            {/* Upload Section - Always visible */}
                            <div className="mt-6 bg-main-th p-5 rounded-xl shadow-sm border border-main-border">
                                <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center">
                                    <HiOutlineUpload className="mr-2 text-xl text-main-blue" />
                                    Upload New JSON File
                                </h2>
                                <form onSubmit={handleSubmit} className='space-y-4'>
                                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                        <div>
                                            <label className="block font-medium text-main-font mb-2">
                                                File Name
                                            </label>
                                            <input
                                                type="text"
                                                value={newFileName}
                                                onChange={(e) => setNewFileName(e.target.value)}
                                                placeholder={selectedFile ? selectedFile.name : "Enter custom name for the JSON file"}
                                                className="w-full p-3 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue bg-main-white shadow-sm"
                                                required={true}
                                            />
                                            <p className="text-[12px] mt-1 text-main-subfont">
                                                If left empty, the original file name will be used.
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block font-medium text-main-font mb-2 flex items-center">
                                                JSON File
                                                <span className='ml-2 text-sm text-main-blue bg-main-thHover px-2 py-0.5 rounded-full'>
                                                    Max size: 1 MB
                                                </span>
                                            </label>
                                            <div className="bg-main-white p-4 border-2 border-dashed border-main-border rounded-lg hover:border-main-blue transition-colors">
                                                <FileUpload
                                                    name="file"
                                                    id="jsonFile"
                                                    onChange={handleFileChange}
                                                    accept="application/json"
                                                    required={true}
                                                    multiple={false}
                                                />
                                                <p className={`text-[12px] mt-3 ${error ? 'text-main-red' : 'text-main-subfont'}`}>
                                                    {error || 'Only valid JSON files are allowed.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='flex space-x-4 py-2'>
                                        <UploadButton />
                                        <ResetButton onClick={resetForm} />
                                    </div>

                                    {selectedFile && (
                                        <div className="bg-main-thHover border border-main-blue rounded-lg p-3 flex items-center">
                                            <FiFile className="text-main-blue mr-2 text-xl" />
                                            <div>
                                                <p className="font-medium text-main-font">Ready to upload:</p>
                                                <p className="text-sm text-main-subfont">{selectedFile.name}</p>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>

                            {/* Manage Files Section - Always visible */}
                            <div className='mt-8'>
                                <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center">
                                    <HiOutlineDocumentText className="mr-2 text-xl text-main-blue" />
                                    Manage JSON Files
                                </h2>
                                <div className='bg-main-bg p-5 rounded-xl shadow-md border border-main-border'>
                                    {jsonFiles.length > 0 ? (
                                        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
                                            {jsonFiles.map((file, index) => (
                                                <div
                                                    className={`${editMode[file._id]
                                                        ? 'bg-main-th border-main-blue ring-2 ring-main-thHover'
                                                        : 'bg-main-white hover:bg-main-bg border-main-border'
                                                        } border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1`}
                                                    key={file._id}
                                                >
                                                    {/* Enhanced Lottie animation display */}
                                                    <div className="flex justify-center mb-4">
                                                        <div className="w-full h-60 bg-main-bg rounded-lg flex items-center justify-center overflow-hidden shadow-inner p-2 border border-main-border">
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <LottieLoad url={file?.j_json} />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* File info and controls */}
                                                    <div className='flex justify-between items-center'>
                                                        {/* Name field */}
                                                        {editMode[file._id] ? (
                                                            <div className="mb-3 w-full">
                                                                <label className="block text-main-font text-sm font-medium mb-1">
                                                                    File Name:
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={editingName[file._id] || ''}
                                                                    onChange={(e) => handleNameChange(file._id, e.target.value)}
                                                                    className="w-full p-2 border border-main-blue rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue bg-main-white"
                                                                    placeholder="Enter file name"
                                                                    required={true}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <h3 className="text-lg font-semibold text-main-font truncate">{file?.j_name}</h3>
                                                        )}

                                                        <div className="flex items-center space-x-2 ml-2">
                                                            {editMode[file._id] ? (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleUpdate(file._id)}
                                                                        className="p-2 bg-main-thHover text-main-blue rounded-full hover:bg-main-th shadow-sm transition-colors"
                                                                        title="Save changes"
                                                                    >
                                                                        <FiCheckCircle className="text-lg" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEditToggle(file._id)}
                                                                        className="p-2 bg-red-50 text-main-red rounded-full hover:bg-red-100 shadow-sm transition-colors"
                                                                        title="Cancel"
                                                                    >
                                                                        <FiXCircle className="text-lg" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleEditToggle(file._id)}
                                                                    className="p-2 bg-main-th text-main-blue rounded-full hover:bg-main-thHover shadow-sm transition-colors"
                                                                    title="Edit file"
                                                                >
                                                                    <FiEdit2 className="text-lg" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Replace JSON file option (only in edit mode) */}
                                                    {editMode[file._id] && (
                                                        <div className="mb-3 mt-3 p-3 bg-main-white rounded-lg border border-main-border">
                                                            <label className="block text-main-font text-sm font-medium mb-2">
                                                                Replace JSON:
                                                            </label>
                                                            <div className="flex items-center">
                                                                <label
                                                                    htmlFor={`replace-file-${file._id}`}
                                                                    className="cursor-pointer flex items-center p-2.5 bg-main-th hover:bg-main-thHover rounded-lg border border-main-border text-main-blue font-medium transition-colors"
                                                                >
                                                                    <HiOutlineUpload className="mr-2 text-lg" />
                                                                    Select New JSON
                                                                </label>
                                                                <input
                                                                    id={`replace-file-${file._id}`}
                                                                    type="file"
                                                                    accept="application/json"
                                                                    onChange={(e) => handleReplacementFileChange(e, file._id)}
                                                                    className="hidden"
                                                                />
                                                            </div>
                                                            {replacementFile[file._id]?.file && (
                                                                <p className="text-xs text-main-blue mt-2 flex items-center bg-main-thHover p-2 rounded-lg">
                                                                    <FiFile className="mr-1" />
                                                                    {replacementFile[file._id].file.name} selected
                                                                </p>
                                                            )}
                                                            {replacementFile[file._id]?.error && (
                                                                <p className="text-xs text-main-red mt-2 bg-red-50 p-2 rounded-lg">
                                                                    {replacementFile[file._id].error}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Timestamps */}
                                                    <div className={`p-3 rounded-lg ${editMode[file._id] ? 'bg-main-white border border-main-border' : 'bg-main-bg'} mt-2`}>
                                                        <div className="flex items-center text-main-subfont text-sm">
                                                            <AiOutlineClockCircle className="mr-2" />
                                                            <span className="font-medium text-main-font">Created:</span>
                                                            <span className="ml-1">{new Date(file.createdAt).toLocaleString()}</span>
                                                        </div>
                                                        {file.updatedAt && file.updatedAt !== file.createdAt && (
                                                            <div className="flex items-center text-main-subfont text-sm mt-1">
                                                                <AiOutlineClockCircle className="mr-2" />
                                                                <span className="font-medium text-main-font">Updated:</span>
                                                                <span className="ml-1">{new Date(file.updatedAt).toLocaleString()}</span>
                                                            </div>
                                                        )}

                                                        {/* Update will add current time */}
                                                        {editMode[file._id] && (
                                                            <div className="flex items-center text-main-blue text-sm mt-2">
                                                                <FiInfo className="mr-2" />
                                                                <p className="italic">
                                                                    Saving will update to: {new Date().toLocaleString()}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bg-main-white p-6 rounded-xl shadow-sm border border-main-border">
                                            <div className="text-center py-8">
                                                <NotFound />
                                                <p className="text-main-subfont mt-4">No JSON files uploaded yet. Use the upload form above to add your first file.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default JsonManager;